import { useMemo, useState } from "react";
import * as Icons from "lucide-react";
import { Search, ChevronDown } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { CTABanner } from "@/components/sections/CTABanner";
import { useAppointment } from "@/context/AppointmentContext";
import { SERVICE_CATEGORIES } from "@/data/services";

export default function ServicesPage() {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState("all");
  const { openDialog } = useAppointment();

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SERVICE_CATEGORIES.map((cat) => ({
      ...cat,
      services: cat.services.filter(
        (s) => !q || s.name.toLowerCase().includes(q) || s.desc.toLowerCase().includes(q)
      ),
    })).filter((cat) => (active === "all" || cat.id === active) && cat.services.length > 0);
  }, [query, active]);

  return (
    <main data-testid="services-page">
      <PageHeader
        eyebrow="Our Services"
        title="Diagnostic Services in Lucknow"
        subtitle="Explore our full range of ultrasound, digital X-ray and pathology investigations — all performed with advanced technology and expert care."
      />

      <section className="py-12 sm:py-16 bg-white">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <Reveal className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                data-testid="service-search"
                placeholder="Search services e.g. thyroid, chest, doppler..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="h-12 pl-12 rounded-full"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
              {[{ id: "all", title: "All" }, ...SERVICE_CATEGORIES].map((c) => (
                <button
                  key={c.id}
                  data-testid={`filter-${c.id}`}
                  onClick={() => setActive(c.id)}
                  className={`whitespace-nowrap rounded-full px-5 h-12 text-sm font-semibold border transition-colors ${
                    active === c.id
                      ? "bg-brand text-white border-brand"
                      : "bg-white text-brand-navy/70 border-border hover:border-brand/40"
                  }`}
                >
                  {c.title}
                </button>
              ))}
            </div>
          </Reveal>

          <div className="mt-12 space-y-14">
            {filtered.length === 0 && (
              <p data-testid="no-results" className="text-center text-muted-foreground py-10">
                No services match "{query}". Try a different search.
              </p>
            )}
            {filtered.map((cat) => {
              const Icon = Icons[cat.icon] || Icons.Activity;
              return (
                <Reveal key={cat.id}>
                  <div data-testid={`services-section-${cat.id}`}>
                    <div className="flex items-center gap-3 mb-5">
                      <span className="grid place-items-center h-12 w-12 rounded-xl bg-brand-soft text-brand">
                        <Icon className="h-6 w-6" strokeWidth={1.6} />
                      </span>
                      <div>
                        <h2 className="font-heading text-2xl font-semibold text-brand-navy">{cat.title}</h2>
                        <p className="text-sm text-muted-foreground">{cat.services.length} investigations</p>
                      </div>
                    </div>
                    <Accordion type="single" collapsible className="grid sm:grid-cols-2 gap-x-6">
                      {cat.services.map((s, i) => (
                        <AccordionItem
                          key={s.name}
                          value={`${cat.id}-${i}`}
                          data-testid={`service-item-${cat.id}-${i}`}
                          className="border-b border-border"
                        >
                          <AccordionTrigger className="font-semibold text-brand-navy hover:no-underline text-left text-[15px]">
                            {s.name}
                          </AccordionTrigger>
                          <AccordionContent className="text-muted-foreground leading-relaxed">
                            <p>{s.desc}</p>
                            <Button
                              onClick={() => openDialog(`${cat.title} — ${s.name}`)}
                              size="sm"
                              variant="ghost"
                              className="mt-3 rounded-full text-brand hover:bg-brand-soft px-4"
                            >
                              Book this test
                            </Button>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTABanner />
    </main>
  );
}
